import {
  CheckCheck,
  CircleDot,
  Clock3,
  PencilLine,
  Play,
  Tag,
  Trash2,
  TriangleAlert,
  UserRound,
  XCircle,
} from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '../../lib/cn';
import { usePagination } from '../../hooks/usePagination';
import { getTaskId, taskPriorities } from '../../lib/appHelpers';
import { formatRecurrence } from '../../lib/recurrence';
import {
  formatRoutineRepeat,
  getRoutineTone,
  isRoutineTask,
} from '../../lib/routines';
import Pagination from '../Pagination';
import RecurrenceEditor from '../ui/RecurrenceEditor';
import SegmentedTabs from '../ui/SegmentedTabs';
import {
  EmptyState,
  inputClassName,
  MetricCard,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

const PRIORITY_TONES = {
  urgent: 'error',
  high: 'warning',
  medium: 'accent',
  low: 'success',
};

const STATUS_TONES = {
  open: 'neutral',
  in_progress: 'accent',
  done: 'success',
  cancelled: 'error',
};

function formatDueDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { label: value, kind: 'future' };
  const now = new Date();
  const diffDays = Math.ceil((date - now) / 86400000);
  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, kind: 'overdue' };
  if (diffDays === 0) return { label: 'Due today', kind: 'today' };
  if (diffDays === 1) return { label: 'Due tomorrow', kind: 'soon' };
  if (diffDays <= 4) return { label: `Due in ${diffDays} days`, kind: 'soon' };
  return {
    label: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date),
    kind: 'future',
  };
}

function toTaskStatusLabel(status) {
  return status === 'in_progress' ? 'In progress' : status || 'Open';
}

function ScopeBadge({ scope }) {
  return (
    <StatusPill tone={scope === 'family' ? 'family' : 'accent'}>
      {scope === 'family' ? 'Family shared' : 'Personal'}
    </StatusPill>
  );
}

function DuePill({ dueInfo }) {
  if (!dueInfo) {
    return null;
  }

  const tone =
    dueInfo.kind === 'overdue'
      ? 'error'
      : dueInfo.kind === 'today'
        ? 'warning'
        : dueInfo.kind === 'soon'
          ? 'accent'
          : 'neutral';

  return <StatusPill tone={tone}>{dueInfo.label}</StatusPill>;
}

function TaskCard({
  task,
  tasksParams,
  onUpdateTask,
  onCompleteTask,
  onEditTask,
  onDeleteTask,
}) {
  const taskId = getTaskId(task);
  const isFocused = taskId && taskId === tasksParams?.focusTaskId;
  const routineMode = isRoutineTask(task) || (isFocused && tasksParams?.view === 'routine');
  const isDone = task?.status === 'done';
  const isCancelled = task?.status === 'cancelled';
  const priority = task?.priority || 'medium';
  const dueInfo = formatDueDate(task?.dueDate);
  const recurrenceLabel = routineMode ? formatRoutineRepeat(task?.recurrence) : formatRecurrence(task?.recurrence);
  const statusTone = STATUS_TONES[task?.status] || 'neutral';
  const priorityTone = PRIORITY_TONES[priority] || 'accent';

  return (
    <article
      className={cn(
        'rounded-[24px] border p-6 shadow-subtle transition lg:p-7',
        isFocused ? 'border-app-accent/35 bg-blue-50/70 shadow-panel' : 'border-app-border/80 bg-app-surface/95',
        (isDone || isCancelled) && 'opacity-80'
      )}
      data-focus-id={taskId ? `task-${taskId}` : undefined}
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <ScopeBadge scope={task?.scope || 'personal'} />
            {routineMode ? <StatusPill tone="family">Routine</StatusPill> : null}
            <StatusPill tone={priorityTone}>{priority}</StatusPill>
            <StatusPill tone={statusTone}>{toTaskStatusLabel(task?.status)}</StatusPill>
          </div>
          {!isDone && !isCancelled ? (
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100"
              type="button"
              title={routineMode ? 'Done for today' : 'Mark complete'}
              onClick={() => onCompleteTask(taskId)}
            >
              <CheckCheck className="h-5 w-5" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        <div>
          <p className={cn('m-0 text-lg font-semibold tracking-tight text-app-ink', (isDone || isCancelled) && 'text-app-muted line-through')}>
            {task?.title || 'Untitled task'}
          </p>
          {task?.description ? (
            <p className="mt-2 text-sm leading-6 text-app-muted">{task.description}</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {task?.assignedTo ? (
            <StatusPill tone="neutral" className="gap-1">
              <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{task.assignedTo}</span>
            </StatusPill>
          ) : null}
          <DuePill dueInfo={dueInfo} />
          {recurrenceLabel ? <StatusPill tone="neutral">{recurrenceLabel}</StatusPill> : null}
          {routineMode ? <StatusPill tone="neutral">{getRoutineTone(task)} reminder</StatusPill> : null}
          {Array.isArray(task?.tags) && task.tags.length ? (
            <StatusPill tone="neutral" className="gap-1">
              <Tag className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{task.tags.join(', ')}</span>
            </StatusPill>
          ) : null}
        </div>

        {!isDone && !isCancelled ? (
          <div className="flex flex-wrap gap-2">
            {task?.status === 'open' ? (
              <button
                className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                type="button"
                onClick={() => onUpdateTask(taskId, { status: 'in_progress' })}
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                <span>Start</span>
              </button>
            ) : null}
            <button
              className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-3 py-2 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
              type="button"
              onClick={() => onCompleteTask(taskId)}
            >
              <CheckCheck className="h-4 w-4" aria-hidden="true" />
              <span>{routineMode ? 'Done for today' : 'Mark done'}</span>
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-700 transition hover:bg-orange-100"
              type="button"
              onClick={() => onUpdateTask(taskId, { status: 'cancelled' })}
            >
              <XCircle className="h-4 w-4" aria-hidden="true" />
              <span>Cancel</span>
            </button>
          </div>
        ) : null}

        <div className="mt-1 flex flex-wrap gap-2 border-t border-app-border/60 pt-5">
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
            type="button"
            onClick={() => onEditTask(task)}
          >
            <PencilLine className="h-4 w-4" aria-hidden="true" />
            <span>Edit</span>
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
            type="button"
            onClick={() => onDeleteTask(taskId)}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            <span>Delete</span>
          </button>
        </div>
      </div>
    </article>
  );
}

function TasksView({
  tasksStatus,
  tasksParams,
  taskFilters,
  taskForm,
  editingTaskId,
  canUseFamilyScope,
  tasksData,
  onTaskFilterChange,
  onRefresh,
  onCreateTask,
  onTaskFormChange,
  onUpdateTask,
  onCompleteTask,
  onEditTask,
  onDeleteTask,
  onCancelTaskEdit,
}) {
  const tasksPagination = usePagination(tasksData, 12);
  const resetTasksPage = tasksPagination.goToFirstPage;

  useEffect(() => {
    resetTasksPage();
  }, [tasksData, resetTasksPage]);

  const openCount = tasksData.filter((task) => task.status === 'open').length;
  const progressCount = tasksData.filter((task) => task.status === 'in_progress').length;
  const doneCount = tasksData.filter((task) => task.status === 'done').length;
  const overdueCount = tasksData.filter((task) => {
    if (task.status === 'done' || task.status === 'cancelled' || !task.dueDate) return false;
    return new Date(task.dueDate) < new Date();
  }).length;

  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <PageHero
        eyebrow="Tasks"
        title="Task workspace"
        lede="Personal and family tasks in one place. Filter by scope, assign work, and update status without leaving the dashboard."
        bearyMessage={tasksStatus.type === 'loading' ? 'Beary is loading family tasks.' : undefined}
        bearyPreset="loading"
        action={
          <SegmentedTabs
            value={taskFilters.scope}
            onValueChange={(value) => onTaskFilterChange('scope', value)}
            ariaLabel="Task scope filter"
            items={[
              { value: 'all', label: 'All' },
              { value: 'personal', label: 'Personal' },
              { value: 'family', label: 'Family' },
            ]}
          />
        }
      />

      <div className="mt-8 grid gap-5 sm:grid-cols-3">
        <MetricCard label="Open" value={openCount} icon={CircleDot} accentClassName="text-app-ink" />
        <MetricCard label="In progress" value={progressCount} icon={Clock3} accentClassName="text-blue-700" />
        <MetricCard label="Done" value={doneCount} icon={CheckCheck} accentClassName="text-emerald-700" />
      </div>

      <div className="mt-8 grid gap-8 xl:grid-cols-[340px_minmax(0,1fr)]">
        <aside className="space-y-8">
          <SectionCard>
            <SectionTitle
              title="Filters"
              copy="Narrow the visible task set without changing the underlying task model."
              action={<StatusPill tone={tasksStatus.type === 'warning' ? 'warning' : tasksStatus.type}>{tasksStatus.message}</StatusPill>}
            />

            <div className="mt-6 space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="task-filter-status">
                  Status
                </label>
                <select
                  id="task-filter-status"
                  value={taskFilters.status}
                  onChange={(event) => onTaskFilterChange('status', event.target.value)}
                  className={inputClassName}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="done">Done</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="all">All statuses</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="task-filter-assigned">
                  Assigned to
                </label>
                <input
                  id="task-filter-assigned"
                  value={taskFilters.assignedTo}
                  onChange={(event) => onTaskFilterChange('assignedTo', event.target.value)}
                  placeholder="Any family member"
                  className={inputClassName}
                />
              </div>

              <div className="rounded-2xl border border-red-200/80 bg-red-50/80 p-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/80 text-red-700">
                    <TriangleAlert className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-red-700">
                      Overdue
                    </p>
                    <p className="mt-1 text-lg font-semibold text-app-ink">{overdueCount}</p>
                  </div>
                </div>
              </div>

              <button
                className="inline-flex items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                type="button"
                onClick={onRefresh}
              >
                Refresh
              </button>
            </div>
          </SectionCard>

          <SectionCard>
            <SectionTitle
              title={editingTaskId ? 'Edit task' : 'New task'}
              copy={
                editingTaskId
                  ? 'Update the selected task in place.'
                  : 'Add the next task without leaving the screen.'
              }
            />

            <form className="mt-6 space-y-5" onSubmit={onCreateTask}>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="task-title">
                  Title
                </label>
                <input
                  id="task-title"
                  value={taskForm.title}
                  onChange={(event) => onTaskFormChange('title', event.target.value)}
                  placeholder="Sign field trip form"
                  required
                  className={inputClassName}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="task-description">
                  Description
                </label>
                <textarea
                  id="task-description"
                  rows="3"
                  value={taskForm.description}
                  onChange={(event) => onTaskFormChange('description', event.target.value)}
                  placeholder="Add context for whoever will complete it."
                  className={inputClassName}
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="task-assigned">
                    Assigned to
                  </label>
                  <input
                    id="task-assigned"
                    value={taskForm.assignedTo}
                    onChange={(event) => onTaskFormChange('assignedTo', event.target.value)}
                    placeholder="Family member"
                    className={inputClassName}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="task-priority">
                    Priority
                  </label>
                  <select
                    id="task-priority"
                    value={taskForm.priority}
                    onChange={(event) => onTaskFormChange('priority', event.target.value)}
                    className={inputClassName}
                  >
                    {taskPriorities.map((priority) => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="task-due-date">
                  Due date
                </label>
                <input
                  id="task-due-date"
                  type="datetime-local"
                  value={taskForm.dueDate}
                  onChange={(event) => onTaskFormChange('dueDate', event.target.value)}
                  className={inputClassName}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink">Recurrence</label>
                <RecurrenceEditor
                  value={taskForm.recurrence}
                  onChange={(value) => onTaskFormChange('recurrence', value)}
                  idPrefix="task-recurrence"
                  requireDateCopy="A recurring task needs a due date. Completing it advances to the next occurrence until the limit is reached."
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink">Scope</label>
                {editingTaskId ? (
                  <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4 text-sm text-app-muted">
                    <p className="m-0 font-semibold text-app-ink">
                      {taskForm.scope === 'family' ? 'Family shared' : 'Personal'}
                    </p>
                    <p className="mt-1">Scope stays fixed while editing an existing task.</p>
                  </div>
                ) : (
                  <>
                    <SegmentedTabs
                      value={taskForm.scope}
                      onValueChange={(value) => onTaskFormChange('scope', value)}
                      ariaLabel="Task scope"
                      items={[
                        { value: 'personal', label: 'Personal' },
                        { value: 'family', label: 'Family', disabled: !canUseFamilyScope },
                      ]}
                    />
                    {!canUseFamilyScope ? (
                      <p className="text-sm leading-6 text-app-muted">
                        Open Family to create a shared household before adding family tasks.
                      </p>
                    ) : null}
                  </>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="task-tags">
                  Tags
                </label>
                <input
                  id="task-tags"
                  value={taskForm.tags}
                  onChange={(event) => onTaskFormChange('tags', event.target.value)}
                  placeholder="school, logistics"
                  className={inputClassName}
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  className="inline-flex items-center justify-center rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
                  type="submit"
                >
                  {editingTaskId ? 'Save task' : 'Create task'}
                </button>
                {editingTaskId ? (
                  <button
                    className="inline-flex items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                    type="button"
                    onClick={onCancelTaskEdit}
                  >
                    Cancel edit
                  </button>
                ) : null}
              </div>
            </form>
          </SectionCard>
        </aside>

        <SectionCard>
          <SectionTitle
            title="Tasks"
            copy={
              taskFilters.scope !== 'all'
                ? `Showing ${taskFilters.scope} tasks`
                : 'All personal and family tasks'
            }
            action={<StatusPill tone="neutral">{tasksPagination.totalItems}</StatusPill>}
          />

          {tasksPagination.totalItems ? (
            <div className="mt-6">
              <div className="grid gap-5 lg:grid-cols-2">
                {tasksPagination.paginatedItems.map((task) => (
                  <TaskCard
                    key={getTaskId(task) || task?.title}
                    task={task}
                    tasksParams={tasksParams}
                    onUpdateTask={onUpdateTask}
                    onCompleteTask={onCompleteTask}
                    onEditTask={onEditTask}
                    onDeleteTask={onDeleteTask}
                  />
                ))}
              </div>
              <Pagination
                currentPage={tasksPagination.currentPage}
                totalPages={tasksPagination.totalPages}
                hasNextPage={tasksPagination.hasNextPage}
                hasPreviousPage={tasksPagination.hasPreviousPage}
                onGoToPage={tasksPagination.goToPage}
                onGoToNext={tasksPagination.goToNextPage}
                onGoToPrevious={tasksPagination.goToPreviousPage}
                onGoToFirst={tasksPagination.goToFirstPage}
                onGoToLast={tasksPagination.goToLastPage}
                startIndex={tasksPagination.startIndex}
                endIndex={tasksPagination.endIndex}
                totalItems={tasksPagination.totalItems}
              />
            </div>
          ) : (
            <div className="mt-6">
              <EmptyState
                title="No tasks matched these filters."
                copy="Switch the scope or status filter, or create a new task from the left rail."
              />
            </div>
          )}
        </SectionCard>
      </div>
    </section>
  );
}

export default TasksView;
